import axios, {
  AxiosError,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from 'axios';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: { startTime: number };
  }
}
import Router from 'next/router';

import { pushEvent } from '@/helpers/analytics';
import { getAccessToken } from '@/helpers/session';
import {
  createCustomStandAloneToast,
  fetchAppKeyValueFromErrorResponse,
} from '@/helpers/utils';
import { UsersApiErrorResponseType } from '@/types/response.type';
const defaultFlags = {
  'teyaseer-baniyasVisible': true,
  'teyaseer-dmtEnabled': true,
  'teyaseer-marketPlace': true,
};
/**
 * public website vendor microservice
 */
export const vendorsPublicApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_VENDORS_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Users microservice
 */
export const usersApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_USERS_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Vendors microservice
 */
export const vendorsApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_VENDORS_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Cost calculator microservice
 */
export const costCalculatorApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_COST_CALCULATOR_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Customer Portal microservice
 */
export const customerPortalApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_USERS_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

export const getFeatureFlagsList = async () => {
  const response = await axios.get('/api/feature-flags', {
    headers: { 'Content-Type': 'application/json' },
  });
  // const data = response.data;
  // if (!data || Object.keys(data).length === 0) {
  //   return defaultFlags;
  // }
  return defaultFlags;
};

const trackApiFailure = (axiosError: AxiosError) => {
  const config = axiosError.config;
  const latency = config?.metadata?.startTime
    ? Date.now() - config.metadata.startTime
    : undefined;
  pushEvent('api_request_failed', {
    endpoint: config?.url || 'unknown',
    status_code: axiosError.response?.status ?? 0,
    ...(latency !== undefined ? { latency_ms: latency } : {}),
  });
};

/**
 * Handle 500 error and show toast
 * @param {InternalAxiosRequestConfig} request
 */
const handleErrorInterceptor500 = async (error: UsersApiErrorResponseType) => {
  const title = fetchAppKeyValueFromErrorResponse(error);
  createCustomStandAloneToast(title);
};

/**
 * Client side API interceptor
 * @param {InternalAxiosRequestConfig} request
 */
const handleClientApiInterceptor = async (
  request: InternalAxiosRequestConfig
) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    request.headers.Authorization = `Bearer ${accessToken}`;
  }
  request.headers.source = 'web';
  request.metadata = { startTime: Date.now() };
  return request;
};

/**
 * Public api interceptor
 * @param {InternalAxiosRequestConfig} request
 */
const publicApiInterceptor = async (request: InternalAxiosRequestConfig) => {
  return request;
};

/**
 * Global api interceptor
 * @param {InternalAxiosRequestConfig} request
 */
const apiInterceptor = async (request: InternalAxiosRequestConfig) => {
  return handleClientApiInterceptor(request);
};

/**
 * Public API error interceptor
 * @param {AxiosError} axiosError
 */
const publicErrorInterceptor = async (
  axiosError: AxiosError<UsersApiErrorResponseType>
) => {
  trackApiFailure(axiosError);
  if (axiosError.response?.status === HttpStatusCode.InternalServerError) {
    handleErrorInterceptor500(axiosError.response.data);
  }

  return Promise.reject(axiosError);
};

/**
 * Customer Portal API error interceptor
 * @param {AxiosError} axiosError
 */
const errorInterceptor = async (
  axiosError: AxiosError<UsersApiErrorResponseType>
) => {
  trackApiFailure(axiosError);
  if (axiosError.response?.status === HttpStatusCode.Unauthorized) {
    Router.push({
      pathname: `/logout`,
      query: { redirectLink: window.location.pathname },
    });
    return Promise.reject(axiosError);
  }

  if (axiosError.response?.status === HttpStatusCode.InternalServerError) {
    handleErrorInterceptor500(axiosError.response.data);
  }

  return Promise.reject(axiosError);
};

/**
 * Global API error interceptor
 * @param {AxiosError} axiosError
 */
const usersApiErrorInterceptor = async (
  axiosError: AxiosError<UsersApiErrorResponseType>
) => {
  trackApiFailure(axiosError);
  if (axiosError.response?.status === HttpStatusCode.Unauthorized) {
    Router.push({
      pathname: `/logout`,
      query: { redirectLink: window.location.pathname },
    });
    return Promise.reject(axiosError);
  }

  if (
    axiosError.response?.status === HttpStatusCode.BadRequest ||
    axiosError.response?.status === HttpStatusCode.InternalServerError ||
    axiosError.response?.status === HttpStatusCode.NotFound
  ) {
    const title = fetchAppKeyValueFromErrorResponse(axiosError.response.data);
    createCustomStandAloneToast(title);
  }

  return Promise.reject(axiosError);
};

// Request interceptors
usersApi.interceptors.request.use(apiInterceptor);
vendorsApi.interceptors.request.use(apiInterceptor);
costCalculatorApi.interceptors.request.use(apiInterceptor);
vendorsPublicApi.interceptors.request.use(publicApiInterceptor);
customerPortalApi.interceptors.request.use(apiInterceptor);
// Response interceptors
usersApi.interceptors.response.use(res => res, usersApiErrorInterceptor);
vendorsApi.interceptors.response.use(res => res, errorInterceptor);
costCalculatorApi.interceptors.response.use(res => res, errorInterceptor);
vendorsPublicApi.interceptors.response.use(res => res, publicErrorInterceptor);
customerPortalApi.interceptors.response.use(res => res, errorInterceptor);
